/**
 * Shared constants and utilities for tax profile components.
 * Used by TaxProfileModal and OnboardingWizard.
 */
import type { TaxProfileType } from "@investhinks/api-client";

/**
 * Tax referrer types for selection (frontend representation).
 * The backend stores SPANISH_AUTONOMO_EMPRESARIAL/PROFESIONAL, but the frontend
 * presents a single SPANISH_AUTONOMO with an activity type selector.
 */
export const TAX_REFERRER_TYPES = [
  { value: "SPANISH_COMPANY", group: "spain", isBusiness: true },
  { value: "SPANISH_AUTONOMO", group: "spain", isBusiness: true },
  { value: "SPANISH_PARTICULAR", group: "spain", isBusiness: false },
  { value: "EU_BUSINESS", group: "eu", isBusiness: true },
  { value: "EU_PRIVATE", group: "eu", isBusiness: false },
  { value: "NON_EU_BUSINESS", group: "noneu", isBusiness: true },
  { value: "NON_EU_PRIVATE", group: "noneu", isBusiness: false },
];

/**
 * Valid tax ID types for each referrer type.
 * Based on PLAN/spec/05-REFERRER-TYPES.md
 */
export const VALID_TAX_ID_TYPES = {
  SPANISH_COMPANY: ["CIF"],
  SPANISH_AUTONOMO: ["NIF", "NIE"],
  SPANISH_PARTICULAR: ["NIF", "NIE"],
  EU_BUSINESS: ["EU_VAT"],
  EU_PRIVATE: ["PASSPORT", "OTHER"],
  NON_EU_BUSINESS: ["OTHER"],
  NON_EU_PRIVATE: ["PASSPORT"],
};

/**
 * Activity types for Spanish autónomos.
 */
export const ACTIVITY_TYPES = ["EMPRESARIAL", "PROFESIONAL"];

/**
 * IRPF rate options for Spanish profesional autónomos.
 */
export const IRPF_RATES = [
  { value: 0.15, label: "15% (Standard)" },
  { value: 0.07, label: "7% (New autónomo)" },
];

/**
 * Get allowed tax ID types for a referrer type.
 */
type TranslationFn = (key: string, fallback?: string) => string;

export const getAllowedTaxIdTypes = (referrerType: string | null | undefined): string[] => {
  if (!referrerType) return [];
  const validTypes: Record<string, string[] | undefined> = VALID_TAX_ID_TYPES;
  return validTypes[referrerType] || [];
};

/**
 * Derive the backend tax type from frontend type + activity type.
 * The backend stores SPANISH_AUTONOMO_EMPRESARIAL or SPANISH_AUTONOMO_PROFESIONAL,
 * but the frontend presents them as a single SPANISH_AUTONOMO with an activity type selector.
 */
const VALID_TAX_PROFILE_TYPES: readonly string[] = [
  "SPANISH_COMPANY",
  "SPANISH_AUTONOMO_EMPRESARIAL",
  "SPANISH_AUTONOMO_PROFESIONAL",
  "SPANISH_PARTICULAR",
  "EU_BUSINESS",
  "EU_PRIVATE",
  "NON_EU_BUSINESS",
  "NON_EU_PRIVATE",
] as const;

function isTaxProfileType(value: string | null | undefined): value is TaxProfileType {
  return value != null && VALID_TAX_PROFILE_TYPES.includes(value);
}

export const deriveBackendTaxType = (
  frontendType: string | null | undefined,
  activityType: string | null | undefined
): TaxProfileType | undefined => {
  if (frontendType === "SPANISH_AUTONOMO") {
    if (activityType === "PROFESIONAL") {
      return "SPANISH_AUTONOMO_PROFESIONAL";
    }
    return "SPANISH_AUTONOMO_EMPRESARIAL";
  }
  if (isTaxProfileType(frontendType)) {
    return frontendType;
  }
  return undefined;
};

/**
 * Derive the frontend tax type from backend type.
 * Converts SPANISH_AUTONOMO_EMPRESARIAL/PROFESIONAL to SPANISH_AUTONOMO.
 */
export const deriveFrontendTaxType = (backendType: string | null | undefined) => {
  if (backendType === "SPANISH_AUTONOMO_EMPRESARIAL" || backendType === "SPANISH_AUTONOMO_PROFESIONAL") {
    return "SPANISH_AUTONOMO";
  }
  return backendType;
};

/**
 * Derive activity type from backend tax type.
 */
export const deriveActivityType = (backendType: string | null | undefined) => {
  if (backendType === "SPANISH_AUTONOMO_PROFESIONAL") {
    return "PROFESIONAL";
  }
  if (backendType === "SPANISH_AUTONOMO_EMPRESARIAL") {
    return "EMPRESARIAL";
  }
  return null;
};

/**
 * Helper to determine which sections to show based on referrer type and activity type.
 */
export const getSectionVisibility = (referrerType: string | null | undefined, activityType: string | null | undefined) => ({
  showCompanyName: ["SPANISH_COMPANY", "EU_BUSINESS", "NON_EU_BUSINESS"].includes(referrerType || ""),
  // Show activity type selector for Spanish autónomos
  showActivityType: referrerType === "SPANISH_AUTONOMO",
  // Show IAE fields for Spanish autónomos
  showIAE: referrerType === "SPANISH_AUTONOMO",
  // Show IRPF only for autónomos with PROFESIONAL activity
  showIRPF: referrerType === "SPANISH_AUTONOMO" && activityType === "PROFESIONAL",
  showVIES: referrerType === "EU_BUSINESS",
  showParticularDeclaration: referrerType === "SPANISH_PARTICULAR",
  showPrivateDeclaration: ["EU_PRIVATE", "NON_EU_PRIVATE"].includes(referrerType || ""),
  showRiskAcknowledgment: referrerType === "SPANISH_PARTICULAR",
  showThresholds: ["SPANISH_PARTICULAR", "EU_PRIVATE", "NON_EU_PRIVATE"].includes(referrerType || ""),
  // Show tax ID type selector only when there are multiple options
  showTaxIdTypeSelector: (() => {
    const types: Record<string, string[] | undefined> = VALID_TAX_ID_TYPES;
    return (referrerType ? types[referrerType] || [] : []).length > 1;
  })(),
});

/**
 * Build grouped options for referrer type dropdown.
 * @param {Function} t - Translation function
 * @returns {Array} Grouped options for react-select
 */
export const buildGroupedReferrerTypeOptions = (t: TranslationFn) => {
  const typeOptions = TAX_REFERRER_TYPES.map((type) => ({
    value: type.value,
    label: t(`referrerTypes.${type.value}`, type.value.replace(/_/g, " ")),
    group: type.group,
    isBusiness: type.isBusiness,
  }));

  return [
    {
      label: t("groups.spain", "Spain"),
      options: typeOptions.filter((o) => o.group === "spain"),
    },
    {
      label: t("groups.eu", "European Union"),
      options: typeOptions.filter((o) => o.group === "eu"),
    },
    {
      label: t("groups.noneu", "Outside EU"),
      options: typeOptions.filter((o) => o.group === "noneu"),
    },
  ];
};

/**
 * Build tax ID type options for the given referrer type.
 * @param {string} referrerType - The referrer type
 * @param {Function} t - Translation function
 * @returns {Array} Options for react-select
 */
export const buildTaxIdTypeOptions = (referrerType: string | null | undefined, t: TranslationFn) => {
  return getAllowedTaxIdTypes(referrerType).map((type: string) => ({
    value: type,
    label: t(`taxIdTypes.${type}`, type),
  }));
};

/**
 * Build activity type options.
 * @param {Function} t - Translation function
 * @returns {Array} Options for react-select
 */
export const buildActivityTypeOptions = (t: TranslationFn) => {
  return ACTIVITY_TYPES.map((type) => ({
    value: type,
    label: t(`activityTypes.${type}`, type),
  }));
};

/**
 * Build IRPF rate options.
 * @param {Function} t - Translation function
 * @returns {Array} Options for react-select
 */
export const buildIrpfRateOptions = (t: TranslationFn) => {
  return IRPF_RATES.map(({ value, label }) => ({
    value,
    label: t(`irpfRates.${value}`, label),
  }));
};

/**
 * Get placeholder for tax ID input based on type.
 */
export const getTaxIdPlaceholder = (taxIdType: string | null | undefined) => {
  switch (taxIdType) {
    case "CIF":
      return "B12345678";
    case "NIF":
      return "12345678Z";
    case "NIE":
      return "X1234567L";
    case "EU_VAT":
      return "DE123456789";
    case "PASSPORT":
      return "AB1234567";
    case "OTHER":
      return "123456789";
    default:
      return "";
  }
};
